/* eslint-disable react/destructuring-assignment */
import {
  Button,
  FormLabel,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaTrash } from 'react-icons/fa';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import Input from '../../Form/Input';

type DynamicProps = {
  key: string;
  value: string;
  id?: string;
};

type DynamicFieldsProps = {
  title: string;
  onUpdate: (data: DynamicProps[]) => void;
  defaultValue?: DynamicProps[];
};

const makeId = () => Math.random().toString(36).substr(2, 9);

export const dynamicFieldSchema = yup.object().shape({
  redirect_data: yup
    .array()
    .of(
      yup.object().shape({
        key: yup
          .string()
          .matches(new RegExp(/^[a-z_]+$/), {
            message: 'Formato inválido',
          })
          .required('Adicione uma chave'),
        value: yup.string().optional(),
      }),
    )
    .nullable(),
});

const DynamicFields: FC<DynamicFieldsProps> = ({
  title,
  onUpdate,
  defaultValue = [{ key: '', value: '' }],
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [props, setProps] = useState<DynamicProps[]>(
    defaultValue.map(p => ({ ...p, id: makeId() })),
  );
  const modalBody = useRef<HTMLDivElement>(null);

  const form = useForm({
    resolver: yupResolver(dynamicFieldSchema),
    mode: 'onBlur',
  });
  const { formState, register, setValue, reset } = form;

  useEffect(() => {
    reset({ redirect_data: props });
  }, [props, reset]);

  const addProp = () => {
    setProps([...props, { key: '', value: '', id: makeId() }]);
    const timeout = setTimeout(() => {
      clearTimeout(timeout);
      modalBody.current?.scrollTo({
        top: 1000,
        behavior: 'smooth',
      });
    }, 100);
  };

  const handleUpdate = () => {
    const updatedProps = props
      .filter(p => p.key)
      .map(p => ({ value: p.value, key: p.key }));
    onUpdate(updatedProps);
  };

  const removeProp = (index: number) => {
    if (props.length === 1) return;
    const newProps = [...props];
    newProps.splice(index, 1);
    setProps(newProps);
    const updatedProps = newProps
      .filter(p => p.key)
      .map(p => ({ value: p.value, key: p.key }));
    onUpdate(updatedProps);
  };
  const { t } = useTranslation();
  return (
    <>
      <Modal onClose={onClose} isOpen={isOpen} isCentered size="xl">
        <ModalOverlay />
        <ModalContent maxH="80svh">
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody ref={modalBody} overflowY="auto">
            <TableContainer>
              <Table variant="simple">
                <Thead borderRadius="14px" overflow="hidden" width="305%">
                  <Tr>
                    <Th>{t('key')}</Th>
                    <Th>{t('value')}</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {props.map((prop, index) => (
                    <Tr key={`tr-${prop.id}`}>
                      <Td>
                        <Input
                          maxW="160px"
                          defaultValue={prop.key}
                          autoComplete="off"
                          {...register(`redirect_data.${index}.key`)}
                          error={
                            (formState.errors?.redirect_data as any)?.[index]
                              ?.key
                          }
                          onChange={e => {
                            const value = e.target.value
                              .toLowerCase()
                              .normalize('NFD')
                              .replace(/[\u0300-\u036f]/g, '')
                              .replace(/[^a-z_]/g, '_');
                            setValue(`redirect_data.${index}.key`, value);
                            const newProps = [...props];
                            newProps[index].key = value;
                            setProps(newProps);
                            handleUpdate();
                          }}
                        />
                      </Td>
                      <Td>
                        <Input
                          defaultValue={prop.value}
                          maxW="160px"
                          autoComplete="off"
                          {...register(`redirect_data.${index}.value`)}
                          error={
                            (formState.errors?.redirect_data as any)?.[index]
                              ?.value
                          }
                          onChange={e => {
                            setValue(
                              `redirect_data.${index}.value`,
                              e.target.value,
                            );
                            const newProps = [...props];
                            newProps[index].value = e.target.value;
                            setProps(newProps);
                            handleUpdate();
                          }}
                        />
                      </Td>
                      <Td>
                        <IconButton
                          tabIndex={-1}
                          aria-label="delete-page"
                          rounded="md"
                          color="red"
                          icon={<FaTrash fontSize={16} />}
                          size="sm"
                          bg="transparent"
                          onClick={() => {
                            removeProp(index);
                          }}
                        />
                      </Td>
                    </Tr>
                  ))}
                  <Tr>
                    <Td border="none">
                      <Button fontWeight="normal" size="xs" onClick={addProp}>
                        {`+ ${t('newField')}`}
                      </Button>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
          </ModalBody>
        </ModalContent>
      </Modal>
      <VStack alignItems="start">
        <FormLabel mb="0">{t('redirectValueField')}</FormLabel>
        <Button
          fontWeight="normal"
          size="sm"
          onClick={onOpen}
          colorScheme="gray"
        >
          {t('add')}
        </Button>
      </VStack>
    </>
  );
};

export default DynamicFields;
